import React, { useState, useEffect, useRef } from 'react';
import '../css/AIChatbot.css';
import { sendChatbotMessage } from '../../api/aiServices';
import { getSessionUUID } from '../lib/AuthToken';
import { useTheme } from '../themeControl/ThemeProvider';
import { useAuthProvider } from '../../providers/AuthProvider';

const AdminChatbot = ({ onClose }) => {
    const { theme } = useTheme();
    const { isDev } = useAuthProvider();
    const [messages, setMessages] = useState([
        {
            id: 1,
            type: 'bot',
            content: 'Hello! I\'m your admin assistant for LogiqOS. I can help you with system analytics, customer management, order oversight, and more. What would you like to know?',
            timestamp: new Date()
        }
    ]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    // Feedback functionality removed - new AI Orchestrator doesn't need feedback
    const [correctionModal, setCorrectionModal] = useState({ show: false, interactionId: null, originalMessage: '' });
    const [correctionText, setCorrectionText] = useState('');
    const [databaseInstructions, setDatabaseInstructions] = useState('');
    const messagesEndRef = useRef(null);

    // Auto-scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const sendMessage = async (messageText = null) => {
        const text = messageText || inputMessage.trim();
        if (!text) return;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: text,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);

        try {
            const response = await sendChatbotMessage(text, messages);
            
            if (response.success) {
                const botMessage = {
                    id: Date.now() + 1,
                    type: 'bot',
                    content: response.response?.message || response.response || 'No response received.',
                    timestamp: new Date(),
                    messageType: response.response.type || 'info',
                    suggested_actions: response.response.suggested_actions || [],
                    system_stats: response.response.system_stats,
                    customer_count: response.response.customer_count,
                    user_type: response.user_type,
                    interaction_id: response.response.interaction_id, // For AI learning feedback
                    learned_from_correction: response.response.learned_from_correction || false,
                    original_question: response.response.original_question,
                    confidence_score: response.response.confidence_score
                };
                setMessages(prev => [...prev, botMessage]);
            } else {
                const errorMessage = {
                    id: Date.now() + 1,
                    type: 'bot',
                    content: 'Sorry, I encountered an error processing your request. Please try again.',
                    timestamp: new Date(),
                    messageType: 'error'
                };
                setMessages(prev => [...prev, errorMessage]);
            }
        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage = {
                id: Date.now() + 1,
                type: 'bot',
                content: 'Connection error. Please check your internet connection and try again.',
                timestamp: new Date(),
                messageType: 'error'
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleQuickAction = (action) => {
        const actionLabels = {
            'system_analytics': 'Show me system analytics and performance metrics',
            'manage_customers': 'Show me customer management overview',
            'view_all_orders': 'Show me all recent orders across customers',
            'warehouse_overview': 'Give me a warehouse operations overview',
            'vehicle_fleet': 'Show me vehicle fleet status',
            'system_help': 'Help me understand system features'
        };
        
        const message = actionLabels[action] || action.replace(/_/g, ' ');
        sendMessage(message);
    };
    
    const handleFeedback = async (interactionId, feedback, correctedResponse = null) => {
        try {
            const sessionToken = getSessionUUID();

            
            const response = await fetch('/ai/chatbot/feedback', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${sessionToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    interaction_id: interactionId,
                    feedback: feedback,
                    corrected_response: correctedResponse
                })
            });
            
            if (response.ok) {

                
                // Feedback tracked successfully
                
                // Show appropriate confirmation message
                const feedbackMessage = feedback === 'helpful' 
                    ? 'Thanks! This helps the AI learn.'
                    : correctedResponse 
                        ? 'Thanks! The AI will learn from your correction.'
                        : 'Thanks! The AI will improve from this feedback.';
                

            }
        } catch (error) {
            console.error('Error sending feedback:', error);
        }
    };

    const handleThumbsDown = (interactionId, originalMessage) => {
        setCorrectionModal({ 
            show: true, 
            interactionId: interactionId, 
            originalMessage: originalMessage 
        });
        setCorrectionText('');
        setDatabaseInstructions('');
    };

    const submitCorrection = async () => {
        const correctionData = {
            corrected_response: correctionText.trim() || null,
            database_instructions: databaseInstructions.trim() || null
        };
        
        if (correctionText.trim() || databaseInstructions.trim()) {
            // Feedback removed - new AI Orchestrator doesn't need corrections
        } else {
            // Feedback removed - new AI Orchestrator doesn't need feedback
        }
        setCorrectionModal({ show: false, interactionId: null, originalMessage: '' });
        setCorrectionText('');
        setDatabaseInstructions('');
    };

    const cancelCorrection = () => {
        setCorrectionModal({ show: false, interactionId: null, originalMessage: '' });
        setCorrectionText('');
        setDatabaseInstructions('');
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const renderMessage = (message) => {
        return (
            <div key={message.id} className={`message ${message.type}-message`}>
                <div className="message-content">
                    <div className={`message-bubble ${message.messageType || ''}`}>
                        {/* Learned Response Indicator */}
                        {message.learned_from_correction && (
                            <div className="learned-indicator">
                                🧠 <strong>Learned Response:</strong> Applied from previous correction
                                {message.confidence_score && (
                                    <span className="confidence-score">
                                        (Confidence: {message.confidence_score}%)
                                    </span>
                                )}
                                {message.original_question && (
                                    <div className="original-question">
                                        Based on: "{message.original_question}"
                                    </div>
                                )}
                            </div>
                        )}
                        <p>{message.content}</p>
                        
                        {/* System Statistics for Admin */}
                        {message.system_stats && (
                            <div className="system-stats">
                                <h4>📊 System Statistics</h4>
                                <p><strong>Total Orders:</strong> {message.system_stats.total_orders}</p>
                                <p><strong>Total Customers:</strong> {message.system_stats.total_customers}</p>
                            </div>
                        )}
                        
                        {/* Customer Count */}
                        {message.customer_count !== undefined && (
                            <div className="customer-stats">
                                <p><strong>Active Customers:</strong> {message.customer_count}</p>
                            </div>
                        )}
                        
                        {/* Suggested Actions */}
                        {message.suggested_actions && message.suggested_actions.length > 0 && (
                            <div className="suggested-actions">
                                {message.suggested_actions.map((action, index) => (
                                    <button
                                        key={index}
                                        className="action-btn"
                                        onClick={() => handleQuickAction(action)}
                                    >
                                        {action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </button>
                                ))}
                            </div>
                        )}
                        
                        <small className="message-time">
                            {formatTimestamp(message.timestamp)}
                        </small>
                        
                        {/* Feedback functionality removed - focusing on AI assistant */}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`chatbot-overlay ${theme}`}>
            <div className={`chatbot-container ${theme}`}>
                {/* Header */}
                <div className="chatbot-header admin-header">
                    <div className="header-left">
                        <img src={require('../img/icon-192.png')} alt="LogiqOS" className="header-logo" />
                        <h3>Admin Assistant</h3>
                        <span className="admin-badge">ADMIN</span>
                    </div>
                    <button className="close-btn" onClick={onClose}>
                        ✕
                    </button>
                </div>

                {/* Messages */}
                <div className="chatbot-messages">
                    {messages.map(renderMessage)}
                    
                    {/* Loading indicator */}
                    {isLoading && (
                        <div className="message bot-message">
                            <div className="message-content">
                                <div className="message-bubble">
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>



                {/* Input */}
                <div className="chatbot-input">
                    <div className="input-container">
                        <textarea
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Ask me about system analytics, customer management, orders..."
                            rows="1"
                            disabled={isLoading}
                        />
                        <button
                            className="send-btn admin-send"
                            onClick={() => sendMessage()}
                            disabled={isLoading || !inputMessage.trim()}
                        >
                            📤
                        </button>
                    </div>
                </div>

                {/* Correction Modal */}
                {correctionModal.show && (
                    <div className="correction-modal-overlay">
                        <div className="correction-modal">
                            <div className="correction-header">
                                <h4>🔧 Help the AI Learn</h4>
                                <button className="modal-close-btn" onClick={cancelCorrection}>✕</button>
                            </div>
                            
                            <div className="correction-content">
                                <div className="original-response">
                                    <label><strong>AI's Response:</strong></label>
                                    <p className="original-text">{correctionModal.originalMessage}</p>
                                </div>
                                
                                <div className="correction-input">
                                    <label><strong>What should the correct response be?</strong> (Optional)</label>
                                    <textarea
                                        value={correctionText}
                                        onChange={(e) => setCorrectionText(e.target.value)}
                                        placeholder="Enter the correct response to help the AI learn..."
                                        rows="3"
                                        className="correction-textarea"
                                    />
                                </div>
                                
                                <div className="database-instructions">
                                    <label><strong>How should the AI get updated data?</strong> (Optional)</label>
                                    <textarea
                                        value={databaseInstructions}
                                        onChange={(e) => setDatabaseInstructions(e.target.value)}
                                        placeholder="e.g., Query warehouse_costs table and count rows for customers with storage..."
                                        rows="3"
                                        className="correction-textarea"
                                    />
                                </div>
                                
                                <p className="correction-help">
                                    💡 <strong>Correct Response:</strong> The right answer for this specific question<br/>
                                    🔍 <strong>Database Instructions:</strong> How to get fresh data when this question is asked again
                                </p>
                            </div>
                            
                            <div className="correction-actions">
                                <button className="cancel-btn" onClick={cancelCorrection}>
                                    Cancel
                                </button>
                                <button className="submit-btn" onClick={submitCorrection}>
                                    {correctionText.trim() ? 'Submit Correction' : 'Mark as Unhelpful'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminChatbot;